package com.eladfinish.bible.contest.ui.components.toolbar

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.LocalTextStyle
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.Layout
import androidx.compose.ui.layout.layoutId
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Constraints
import androidx.compose.ui.unit.dp
import androidx.compose.ui.util.fastFirst
import com.eladfinish.bible.contest.ui.components.divider.AppDividerHorizontal
import kotlin.math.max
import kotlin.math.roundToInt

@Composable
fun AppToolbar(
    title: @Composable () -> Unit,
    isCenterTitle: Boolean = false,
    modifier: Modifier = Modifier,
    navigationIcon: @Composable (() -> Unit)? = null,
    actions: @Composable RowScope.() -> Unit = {},
    showDivider: Boolean = false,
) {
    ToolbarWithDivider(
        showDivider = showDivider,
        modifier = modifier,
    ) {
        AppToolbar(
            navigationIcon = navigationIcon,
            title = title,
            isCenterTitle = isCenterTitle,
            actions = actions,
            modifier = modifier,
        )
    }
}

@Composable
private fun AppToolbar(
    navigationIcon: @Composable (() -> Unit)?,
    title: @Composable () -> Unit,
    isCenterTitle: Boolean,
    actions: @Composable RowScope.() -> Unit,
    modifier: Modifier = Modifier,
) {
    val minHeight = LocalDensity.current.run {
        if (isCenterTitle) {
            44.dp.toPx()
        } else {
            56.dp.toPx()
        }
    }

    val verticalOffset = LocalDensity.current.run {
        0.dp.toPx()
    }.toInt()

    val titleHorizontalArrangement = if (isCenterTitle) Arrangement.Center else Arrangement.Start

    Layout(
        {
            Box(
                Modifier
                    .layoutId("navigationIcon")
            ) {
                navigationIcon?.invoke()
            }
            Box(
                Modifier
                    .layoutId("title")
                    .padding(
                        horizontal = 16.dp,
                        vertical = if (isCenterTitle) {
                            10.dp
                        } else {
                            16.dp
                        }
                    )
            ) {
                CompositionLocalProvider(
                    LocalContentColor provides MaterialTheme.colorScheme.onSurface,
                    LocalTextStyle provides LocalTextStyle.current
                        .merge(MaterialTheme.typography.titleLarge)
                        .copy(textAlign = if (isCenterTitle) TextAlign.Center else TextAlign.Start),
                    content = title,
                )
            }
            Row(
                modifier = Modifier
                    .padding(end = 8.dp)
                    .layoutId("actionIcons"),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                actions()
            }
        },
        modifier = modifier.padding(horizontal = 0.dp),
    ) { measurables, constraints ->
        val navigationIconPlaceable =
            measurables.fastFirst { it.layoutId == "navigationIcon" }
                .measure(constraints.copy(minWidth = 0))

        val actionIconsPlaceable =
            measurables.fastFirst { it.layoutId == "actionIcons" }
                .measure(constraints.copy(minWidth = 0))

        val maxTitleWidth = if (constraints.maxWidth == Constraints.Infinity) {
            constraints.maxWidth
        } else {
            val totalActionsWidth = if (isCenterTitle) {
                // For center alignment, the toolbar’s right and left title insets should be equal to avoid title shifting.
                maxOf(navigationIconPlaceable.width, actionIconsPlaceable.width) * 2
            } else {
                navigationIconPlaceable.width + actionIconsPlaceable.width
            }
            (constraints.maxWidth - totalActionsWidth).coerceAtLeast(0)
        }
        val titlePlaceable =
            measurables.fastFirst { it.layoutId == "title" }
                .measure(constraints.copy(minWidth = 0, maxWidth = maxTitleWidth))

        val layoutHeight = max(minHeight.roundToInt(), titlePlaceable.height)
        val layoutMinHeight = minHeight.roundToInt()

        layout(constraints.maxWidth, layoutHeight) {
            // Navigation icon
            navigationIconPlaceable.placeRelative(
                x = 0,
                y = ((layoutMinHeight - navigationIconPlaceable.height) / 2) - verticalOffset
            )

            // Title
            titlePlaceable.placeRelative(
                x = when (titleHorizontalArrangement) {
                    Arrangement.Center -> {
                        var baseX = (constraints.maxWidth - titlePlaceable.width) / 2
                        if (baseX < navigationIconPlaceable.width) {
                            // May happen if the navigation is wider than the actions and the
                            // title is long. In this case, prioritize showing more of the title by
                            // offsetting it to the right.
                            baseX += (navigationIconPlaceable.width - baseX)
                        } else if (baseX + titlePlaceable.width >
                            constraints.maxWidth - actionIconsPlaceable.width
                        ) {
                            // May happen if the actions are wider than the navigation and the title
                            // is long. In this case, offset to the left.
                            baseX += ((constraints.maxWidth - actionIconsPlaceable.width) -
                                    (baseX + titlePlaceable.width))
                        }
                        baseX
                    }

                    Arrangement.End ->
                        constraints.maxWidth - titlePlaceable.width - actionIconsPlaceable.width
                    // Arrangement.Start.
                    // A 12.dp padding will make sure the title is offset in case the
                    // navigation icon is missing.
                    else -> max(12.dp.roundToPx(), navigationIconPlaceable.width)
                },
                y = ((layoutMinHeight - titlePlaceable.height) / 2) - verticalOffset
            )

            // Action icons
            actionIconsPlaceable.placeRelative(
                x = constraints.maxWidth - actionIconsPlaceable.width,
                y = ((layoutMinHeight - actionIconsPlaceable.height) / 2) - verticalOffset
            )
        }
    }
}


@Composable
private fun ToolbarWithDivider(
    showDivider: Boolean,
    modifier: Modifier = Modifier,
    toolbarContent: @Composable () -> Unit,
) {
    Column(modifier) {
        toolbarContent()

        if (showDivider) {
            AppDividerHorizontal(color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.05f))
        }
    }
}
